import { z } from 'zod';

/**
 * Config drift — the review-and-reconcile surface over the nightly check.
 *
 * The DETECTOR (services/olt/drift.ts) is the only author of findings and of
 * fixes: a finding is fixable exactly when the detector stored rendered chassis
 * commands beside it (the Huawei diff does), and review-only when it stored
 * evidence but no commands (the ZTE line comparison does — a safe ZTE re-apply
 * reads the chassis before composing, which is the ONU page's Resync, not a
 * replay of stored lines). This surface never composes chassis grammar of its
 * own; it lists, shows both sides, applies what was recorded, or dismisses
 * with a reason.
 */

export const driftFindingStatusSchema = z.enum(['open', 'fixed', 'fix_failed', 'dismissed']);
export type DriftFindingStatus = z.infer<typeof driftFindingStatusSchema>;

/** One drift finding, as the Drift page lists it. */
export const driftFindingSchema = z.object({
  id: z.string(),
  oltId: z.string().nullable(),
  /** Joined for display; null when the OLT row is gone. */
  oltName: z.string().nullable(),
  /** Present on per-ONU findings, so the UI can link without parsing the subject. */
  onuId: z.string().nullable(),
  deviceId: z.string().nullable(),
  /** e.g. `gpon-onu_1/3/15:11 (Connect - Office)` or `0/1/0:5 (name)`. */
  subject: z.string(),
  summary: z.string(),
  status: driftFindingStatusSchema,
  createdAt: z.number().int(),
  /**
   * True when the detector stored rendered fix commands on this finding.
   * Independent of status — apply is offered only while the finding is open.
   */
  fixable: z.boolean(),
});
export type DriftFinding = z.infer<typeof driftFindingSchema>;

/** One OLT's row on the fleet summary — every OLT in scope appears, clean ones included. */
export const driftOltSummarySchema = z.object({
  oltId: z.string(),
  oltName: z.string(),
  /** The operator-facing number (/olts/1); null on rows that predate numbering. */
  oltNumber: z.number().int().nullable(),
  vendor: z.string(),
  open: z.number().int(),
  fixable: z.number().int(),
  reviewOnly: z.number().int(),
  /** How long the oldest open finding has stood. Null when the OLT is clean. */
  oldestOpenAt: z.number().int().nullable(),
  latestOpenAt: z.number().int().nullable(),
});
export type DriftOltSummary = z.infer<typeof driftOltSummarySchema>;

export const driftSummarySchema = z.object({
  olts: z.array(driftOltSummarySchema),
  totals: z.object({
    open: z.number().int(),
    fixable: z.number().int(),
    reviewOnly: z.number().int(),
  }),
});
export type DriftSummary = z.infer<typeof driftSummarySchema>;

/**
 * Both sides of one finding, plus the recorded fix.
 *
 * `lines` is the ZTE detector's shape: the rendered intent (`expected`), the
 * subset the chassis was missing (`missing`), and — on findings recorded since
 * the chassis side started being captured — the ONU's own comparable lines
 * (`chassis`, null on older rows). `changes` is the Huawei detector's shape:
 * one difference per line in the operator's words, each naming both sides.
 */
export const driftFindingDetailSchema = z.object({
  finding: driftFindingSchema,
  lines: z
    .object({
      expected: z.array(z.string()),
      missing: z.array(z.string()),
      chassis: z.array(z.string()).nullable(),
    })
    .nullable(),
  changes: z.array(z.string()).nullable(),
  fix: z.object({
    fixable: z.boolean(),
    /** The detector's commands, verbatim — exactly what apply sends. */
    commands: z.array(z.string()),
    /** Why apply is not offered, when it is not. */
    reviewOnly: z.string().nullable(),
  }),
  /** Who closed it without acting, and why. */
  dismissed: z
    .object({ at: z.number().int(), by: z.string(), reason: z.string().nullable() })
    .nullable(),
  /** The apply (or the OLT log's reconcile) that closed it, with any chassis rejections. */
  applied: z
    .object({ at: z.number().int(), by: z.string(), rejected: z.array(z.string()).nullable() })
    .nullable(),
});
export type DriftFindingDetail = z.infer<typeof driftFindingDetailSchema>;

/** Dismissing records who, when and why — the row is evidence later. */
export const driftDismissSchema = z.object({
  reason: z.string().trim().min(1, 'Say why this drift is being dismissed.').max(500),
});
export type DriftDismissRequest = z.infer<typeof driftDismissSchema>;

export const driftApplySchema = z.object({
  dryRun: z.boolean().default(false),
});
export type DriftApplyRequest = z.infer<typeof driftApplySchema>;

export const driftListQuerySchema = z.object({
  oltId: z.string().optional(),
  status: driftFindingStatusSchema.or(z.literal('all')).default('open'),
});
export type DriftListQuery = z.infer<typeof driftListQuerySchema>;

/**
 * The ONUs on one chassis with open drift, gathered so a single bulk resync can
 * reconcile them all at once.
 *
 * A resync is the ONLY safe re-apply this platform performs — it reads the
 * chassis (numbering, vports) before it writes, which is exactly why the drift
 * board already sends a single ZTE finding to the ONU page's Resync rather than
 * replaying stored lines blind. This is that same fix at fleet scale: the list
 * of drifted ONUs handed to a bulk `onu_resync` plan, which the operator
 * reviews and runs through the normal bulk flow (per-ONU outcomes, one CLI
 * session per chassis, stop/continue). Findings clear when the next nightly
 * check confirms the chassis now matches — the detector stays the only author
 * of a finding's `fixed` state.
 *
 * `onuIds` is capped at the bulk tick-list limit; `capped` says the chassis had
 * more drifted ONUs than one run takes, so the operator runs it again for the
 * rest rather than a silently shortened selection reconciling ONUs nobody saw.
 */
export const driftReconcilableSchema = z.object({
  oltId: z.string(),
  oltName: z.string(),
  onuIds: z.array(z.string()),
  /** Distinct drifted ONUs on the chassis, before the cap. */
  total: z.number().int(),
  capped: z.boolean(),
});
export type DriftReconcilable = z.infer<typeof driftReconcilableSchema>;
